import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Select({ name, value, defaultValue, options, onChange, className, searchable, placeholder }) {
    const [isOpen, setIsOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? options[0]?.value);
    const [searchQuery, setSearchQuery] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    const currentValue = value !== undefined ? value : internalValue;

    // Compute fixed position from trigger button bounding rect
    const computeDropdownPos = () => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
        });
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                // also allow clicks inside the portal dropdown
                const portal = document.getElementById('select-dropdown-portal');
                if (portal && portal.contains(event.target)) return;
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset search & focus on open/close
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
        } else {
            computeDropdownPos();
            if (searchable && searchRef.current) {
                setTimeout(() => searchRef.current?.focus(), 50);
            }
        }
    }, [isOpen]);

    // Recompute on scroll/resize while open
    useEffect(() => {
        if (!isOpen) return;
        const update = () => computeDropdownPos();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [isOpen]);

    const handleSelect = (optionValue) => {
        setInternalValue(optionValue);
        setIsOpen(false);
        if (onChange) onChange(optionValue);
    };

    // Lotin → Kirill transliteratsiya (qidirishda lotin bilan kirill nomlarini topish uchun)
    const cyrillicToLatin = (str) => {
        const map = {
            'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'yo','ж':'j','з':'z',
            'и':'i','й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r',
            'с':'s','т':'t','у':'u','ф':'f','х':'x','ц':'ts','ч':'ch','ш':'sh',
            'щ':'sch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya',
            'ғ':'g','қ':'q','ҳ':'h','ў':'o','ӯ':'u',
        };
        return str.toLowerCase().split('').map(c => map[c] ?? c).join('');
    };

    const matchesSearch = (label, query) => {
        const lq = query.toLowerCase();
        const ll = String(label).toLowerCase();
        if (ll.includes(lq)) return true;           // to'g'ridan-to'g'ri mos
        return cyrillicToLatin(ll).includes(lq);    // kirill → lotinga o'girib mos
    };

    const filteredOptions = searchable && searchQuery
        ? options.filter(o => matchesSearch(o.label, searchQuery))
        : options;

    const selectedOption = options.find(o => String(o.value) === String(currentValue));
    const displayLabel = selectedOption ? selectedOption.label : (placeholder || options[0]?.label);

    const dropdown = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    id="select-dropdown-portal"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.12 }}
                    style={dropdownStyle}
                    className="bg-surface rounded-xl shadow-2xl border border-border overflow-hidden"
                >
                    {/* Search input */}
                    {searchable && (
                        <div className="px-2 pt-2 pb-1 bg-surface border-b border-border/50">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Qidirish..."
                                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-border bg-surface-2 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-brand/30"
                                    onClick={e => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}
                    <div className="max-h-56 overflow-y-auto custom-scrollbar py-1">
                        {filteredOptions.length === 0 ? (
                            <p className="px-3 py-4 text-center text-sm text-muted">Natija topilmadi</p>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onMouseDown={e => { e.preventDefault(); handleSelect(option.value); }}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                                        String(currentValue) === String(option.value)
                                        ? 'bg-brand/10 text-brand font-semibold'
                                        : 'text-primary hover:bg-surface-2'
                                    }`}
                                >
                                    {option.label}
                                    {String(currentValue) === String(option.value) && <Check className="w-4 h-4 flex-shrink-0" />}
                                </button>
                            ))
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            <div className="relative w-full" ref={containerRef}>
                <input type="hidden" name={name} value={currentValue ?? ''} />
                <button
                    type="button"
                    onClick={() => setIsOpen(prev => !prev)}
                    className={`flex items-center justify-between text-left focus:outline-none w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-primary ${className}`}
                >
                    <span className={`truncate ${!selectedOption && placeholder ? 'text-muted' : ''}`}>
                        {displayLabel}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-muted transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
            </div>
            {createPortal(dropdown, document.body)}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: var(--muted); }
            `}</style>
        </>
    );
}
